import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { combineLatest, concatMap, map, Observable, of, shareReplay } from "rxjs";

import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { MediaService } from "../media/media.service";
import { GateFaultUtilsService } from "../../utils/gate-fault-utils/gate-fault-utils.service";

import { Reference } from "../../../models/shared/refs";
import { FaultReportData } from "../../../models/api/fault/faultReportData";
import { GateFaultDTO, GateFault } from "../../../models/api/fault/gateFault";
import { Media } from "../../../models/api/media";
import { DOCUMENT } from "@angular/common";
import { transformManufacturerName } from "../../../shared/utils/utils";
import { CursorPagination, CursorPaginationDto } from "../../../models/api/pagination";
import { Sort } from "../../../models/api/sort";

@Injectable({
  providedIn: "root",
})
export class GateFaultService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private mediaService = inject(MediaService);
  private gateFaultUtils = inject(GateFaultUtilsService);
  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  /**
   * Posts a repair request with the given gate fault id and optional notes.
   * If files are given, they will be uploaded as media for the repair request.
   * @param gateFaultId The id of the gate fault for which a repair request should be posted.
   * @param notes Optional notes for the repair request.
   * @param files Optional files to be uploaded as media for the repair request.
   * @returns The reference of the created repair request.
   */
  public requestRepair(gateFaultId: string, notes?: string, files?: File[]) {
    const gatefault = { gateFault: { id: gateFaultId }, notes };
    const requestPost$ = this.http
      .post<Reference>(environment.baseUrl + `/repairRequest`, gatefault)
      .pipe(this.apiErrorHandler.handle("repair_request"));
    return requestPost$.pipe(
      concatMap((repairRequest) => {
        if (!files || files.length === 0) {
          return of(repairRequest);
        } else if (repairRequest.id) {
          return this.uploadRepairRequestMedia(files, repairRequest.id);
        } else {
          return of(null);
        }
      })
    );
  }

  /**
   * Fetches gate faults. If assetId and assetType are given, only gate faults associated with the given asset will be returned.
   * @param assetId The id of the asset for which to fetch gate faults.
   * @param assetType The type of the asset for which to fetch gate faults.
   * @param active Filter for active or inactive gate faults
   * @returns An observable that emits an array of gate faults.
   */
  public getGateFaults(assetId?: string, assetType?: string, active?: boolean): Observable<GateFault[]> {
    return this.fetchGateFaults(assetId, assetType, active).pipe(
      map((res) => {
        return res.items.map((gateFault) => {
          return this.gateFaultUtils.convertGateFault(gateFault);
        });
      }),
      shareReplay()
    );
  }

  /**
   * Retrieves fault reporting data for a specified asset.
   * Transforms and filters the fetched gate faults into a fault reporting format.
   *
   * @param assetId The optional id of the asset for which to fetch fault reporting data.
   * @param assetType The optional type of the asset for which to fetch fault reporting data.
   * @param active Filter for active or inactive gate faults
   * @param pagination Define how many faults you want to see and the starting point
   * @param sort Sort by a given column
   * @returns An observable that emits an array of transformed and filtered FaultReportData objects.
   */
  public getFaultReportingData(
    assetId?: string,
    assetType?: string,
    active?: boolean,
    pagination?: CursorPagination,
    sort?: Sort
  ): Observable<CursorPaginationDto<FaultReportData, null>> {
    return this.fetchGateFaults(assetId, assetType, active, pagination, sort).pipe(
      map((gateFaults) => {
        return {
          meta: gateFaults.meta,
          total: gateFaults.total,
          items: gateFaults.items
            .map((gateFault) => {
              return {
                id: gateFault.id,
                faultText: gateFault.faultText,
                faultCode: gateFault.faultCode ?? "",
                faultType: gateFault.faultType,
                triggeredAt: gateFault.triggered_at,
                clearedAt: gateFault.cleared_at,
                control:
                  transformManufacturerName(gateFault.gateSystem?.controlManufacturer?.label) +
                  " " +
                  gateFault.gateSystem?.controlModel?.label,
                gateSystem: gateFault.gateSystem,
              };
            })
            .filter((fault) => fault.faultType),
        };
      })
    );
  }

  /**
   * Retrieves fault reporting data for a specified asset as CSV.
   *
   * @param assetId The optional id of the asset for which to fetch fault reporting data.
   * @param assetType The optional type of the asset for which to fetch fault reporting data.
   * @param active Filter for active or inactive gate faults
   * @returns An observable that emits a FaultReportData csv blob.
   */
  public getGateFaultExport(assetId?: string, assetType?: string, active?: boolean): Observable<HttpResponse<Blob>> {
    let params = new HttpParams();
    const lang = this.localStorage?.getItem("lang");

    if (assetType) params = params.set("assetType", assetType);
    if (assetId) params = params.set("assetId", assetId);
    if (active) params = params.set("active", active);
    if (lang) params = params.set("lang", lang);

    return this.http
      .get(environment.baseUrl + `/gateFaults/export`, {
        params,
        observe: "response",
        responseType: "blob",
      })
      .pipe(this.apiErrorHandler.handle("gate_fault"));
  }

  /**
   * Uploads the given files as media for the given repair request.
   * @param files The files to be uploaded.
   * @param repairRequestId The id of the repair request.
   * @returns An observable that completes when all files have been uploaded.
   * The observable emits an array of media objects, each representing one of the uploaded files.
   */
  private uploadRepairRequestMedia(files: File[], repairRequestId: string) {
    const requestPosts$: Observable<Media[]>[] = [];
    files.forEach((file) => {
      const requestPost$ = this.mediaService.uploadMedia(
        "REPAIR_REQUEST",
        repairRequestId,
        "REPAIR_ATTACHMENT",
        file,
        file.name
      );
      requestPosts$.push(requestPost$);
    });
    return combineLatest(requestPosts$);
  }

  /**
   * Fetches gate faults.
   * If assetId and assetType are given, only gate faults associated with the given asset will be returned.
   * @param assetId The id of the asset for which to fetch gate faults.
   * @param assetType The type of the asset for which to fetch gate faults.
   * @param active Filter for active or inactive gate faults
   * @param pagination Define how many faults you want to see and the starting point
   * @param sort Sort by a given column
   * @returns An observable that emits an array of gate faults.
   */
  private fetchGateFaults(
    assetId?: string,
    assetType?: string,
    active?: boolean,
    pagination?: CursorPagination,
    sort?: Sort
  ): Observable<CursorPaginationDto<GateFaultDTO, null>> {
    let params = new HttpParams();
    const lang = this.localStorage?.getItem("lang");

    if (assetType) params = params.set("assetType", assetType);
    if (assetId) params = params.set("assetId", assetId);
    if (active) params = params.set("active", active);
    if (lang) params = params.set("lang", lang);
    if (pagination?.cursor) params = params.set("pagination[cursor]", pagination.cursor);
    params = params
      .set("pagination[pageSize]", pagination?.pageSize ?? 50)
      .set("sort[column]", sort?.column ?? "triggeredAt")
      .set("sort[descending]", sort?.descending ?? false);

    return this.http
      .get<Omit<CursorPaginationDto<GateFaultDTO, null>, "meta">>(environment.baseUrl + `/gateFaults`, {
        params,
      })
      .pipe(
        this.apiErrorHandler.handle("gate_fault"),
        map((response) => ({ ...response, meta: null }))
      );
  }
}
